import React, { ReactNode, useState } from "react";
import styled, { css } from "styled-components";

import { Color } from "ts/enums/color";
import { ExpandCollapseButton } from "components/_buttons/ExpandCollapseButton";
import { Skeleton } from "components/Skeleton";
import { Tooltip } from "components/Tooltip";
import { Icon, IconType } from "components/_icons/Icon";
import { Resource, Text } from "components/Text";
import { TextType } from "ts/TextType";

type CustomStyles = {
  backgroundColor?: string;
  removePaddingButtom?: boolean;
  padding?: number;
};

type Props = {
  titleLeftResource?: Resource;
  titleRightResource?: Resource;
  titleRightComponent?: ReactNode | TextType;
  children: ReactNode;
  isLoading?: boolean;
  tooltipCaptionKey?: Resource;
  titleBottom?: ReactNode | string;
  hideBoxShadow?: boolean;
  hideHeaderBorder?: boolean;
  customStyles?: CustomStyles;
  isSectionExpanded?: boolean;
  isDisabled?: boolean;
};

export const CollapsibleSection = ({
  titleLeftResource,
  titleRightResource,
  titleRightComponent,
  children,
  isLoading,
  tooltipCaptionKey,
  titleBottom,
  hideBoxShadow,
  hideHeaderBorder,
  isSectionExpanded = false,
  customStyles,
  isDisabled,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(isSectionExpanded);

  const handleExpand = () => {
    if (isLoading || isDisabled) return;
    setIsExpanded((expand) => !expand);
  };

  return (
    <StyledCollapsibleSection
      showBoxShadow={isExpanded && !hideBoxShadow}
      backgroundColor={customStyles?.backgroundColor}
      padding={customStyles?.padding}
      isDisabled={isDisabled}
    >
      <StyledHeaderContainer
        isExpanded={isExpanded}
        hideHeaderBorder={hideHeaderBorder}
        removePaddingButtom={customStyles?.removePaddingButtom}
      >
        <StyledClickableHeader data-no-dnd="true" onClick={handleExpand}>
          <ExpandCollapseButton
            isCollapsed={!isExpanded}
            customWidth={16}
            customHeight={16}
            isLoading={isLoading}
            onClick={() => undefined}
            disabled={isDisabled}
          />
          {isLoading ? (
            <StyledSkeletonContainer className="pulse">
              <Skeleton width={150} height={18} backgroundColor={Color.neutral10} />
            </StyledSkeletonContainer>
          ) : (
            <StyledButtonText isDisabled={isDisabled}>
              <Text resource={titleLeftResource} />
            </StyledButtonText>
          )}
        </StyledClickableHeader>
        {isLoading ? (
          <StyledSkeletonContainer className="pulse">
            <Skeleton width={150} height={18} backgroundColor={Color.neutral10} />
          </StyledSkeletonContainer>
        ) : titleRightResource ? (
          <StyledRightContainer>
            <span>
              <Text resource={titleRightResource} />
            </span>
            {tooltipCaptionKey && (
              <div data-tooltip-id="empty-cell-info">
                <Icon type={IconType.info} color={Color.gray50} size={12} />
                <Tooltip
                  tooltipId="empty-cell-info"
                  content={<Text resource={tooltipCaptionKey} />}
                  fontSize="0.785em"
                />
              </div>
            )}
          </StyledRightContainer>
        ) : (
          titleRightComponent
        )}
        {titleBottom}
      </StyledHeaderContainer>
      {!isLoading && (
        <StyledChildrenContainer isExpanded={isExpanded}>{children}</StyledChildrenContainer>
      )}
    </StyledCollapsibleSection>
  );
};

const StyledCollapsibleSection = styled.div<{
  showBoxShadow: boolean;
  backgroundColor?: string;
  padding?: number;
  isDisabled: boolean;
}>`
  padding: ${({ padding }) => (padding ? padding : 16)}px;
  border-radius: 2px;
  border: 1px solid ${Color.blue20};
  background-color: ${Color.white};
  font-size: 15px;
  background-color: ${({ backgroundColor }) => backgroundColor || Color.white};

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      cursor: default;
    `}

  ${({ showBoxShadow }) =>
    showBoxShadow &&
    css`
      box-shadow: 0px 3px 10px 0px rgba(53, 56, 77, 0.2);
      transition: box-shadow 200ms ease-in;
    `}
`;

const StyledHeaderContainer = styled.div<{
  isExpanded: boolean;
  hideHeaderBorder: boolean;
  removePaddingButtom: boolean;
}>`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid transparent;
  flex-wrap: wrap;
  row-gap: 10px;

  ${({ isExpanded, hideHeaderBorder, removePaddingButtom }) =>
    isExpanded &&
    css`
      padding-bottom: ${removePaddingButtom ? "0px" : "10px"};
      border-bottom: ${!hideHeaderBorder && `1px solid ${Color.blue20}`};
      transition: 200ms padding-bottom ease-in, 200ms border-bottom ease-in;
    `}

  span {
    font-weight: bold;
  }
`;

const StyledClickableHeader = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
  user-select: none;
  flex: 1;
`;

const StyledRightContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
`;

const StyledSkeletonContainer = styled.div`
  height: 18px;
`;

const StyledChildrenContainer = styled.div<{ isExpanded: boolean }>`
  opacity: ${({ isExpanded }) => (isExpanded ? 1 : 0)};
  max-height: ${({ isExpanded }) => (isExpanded ? "100000px" : "0px")};
  overflow: ${({ isExpanded }) => (isExpanded ? "visible" : "hidden")};
  cursor: default;
  transition: ${({ isExpanded }) =>
    isExpanded ? "opacity 200ms ease-in, max-height 200ms ease-in" : "max-height"};
`;

const StyledButtonText = styled.span<{ isDisabled?: boolean }>`
  ${({ isDisabled }) =>
    isDisabled &&
    css`
      color: ${Color.gray30};
    `}
`;
